import { generateBaseProjection, getTeamName } from '@bo-utils/trading';
import { DateTime } from 'luxon';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueProjectionAttributes } from '@library/types/leagues';
import type { LeagueTeamPlayers } from '@library/types/player';
import type { OddsResponse } from '@library/types/simulations';
import { useGetSimulationOddsQuery } from '@bo-services/simulationsApi';
import { useMemo } from 'react';

export function useSimulationOdds({
  allMarkets,
  durations = [],
  existingProjections,
  gameId,
  gameScheduledAt,
  leagueId,
  playersFromRoster,
  simulationId,
  statTypes = []
}: {
  allMarkets: Array<{
    league_id: string;
    market: string;
    market_id: string;
    stat_type_id: number;
  }>;
  durations?: Array<LeagueProjectionAttributes>;
  existingProjections: Array<GamePlayerProjectionTrading>;
  gameId: string;
  gameScheduledAt: string;
  leagueId: string;
  playersFromRoster: LeagueTeamPlayers;
  simulationId: string;
  statTypes?: Array<LeagueProjectionAttributes>;
}): {
  isSimulationOddsError: boolean;
  isSimulationOddsLoading: boolean;
  simulationGeneratedProjections?: Array<GamePlayerProjectionTrading>;
  simulationOdds?: OddsResponse;
} {
  const {
    data: simulationOddsData,
    isError: isSimulationOddsError,
    isFetching,
    isLoading
  } = useGetSimulationOddsQuery({
    gameId,
    leagueId,
    simulationId
  });

  const isSimulationOddsLoading = isLoading || isFetching;

  // use for mock until endpoints ready

  const simulationGeneratedProjections = useMemo(
    () =>
      simulationOddsData?.players.flatMap((oddsForPlayer) => {
        const {
          markets: playerMarketOdds,
          prizepicks_player_id: playerId,
          player_name: playerName
        } = oddsForPlayer;

        // TODO should find by ID when possible...
        const currentPlayerFromRoster = playersFromRoster.find(
          (prospectivePlayer) =>
            prospectivePlayer.name.toLocaleUpperCase() ===
            playerName.toLocaleUpperCase()
        );

        const durationId = durations.find(
          (duration) => duration.leagueProjectionFilterName === 'Full'
        )?.leagueProjectionFilterId;

        const hasNecessaryData = !!currentPlayerFromRoster && !!durationId;

        if (!hasNecessaryData) {
          return [];
        }

        const {
          foreign_id: foreignId,
          market: teamMarket,
          position,
          team,
          team_name: rosterTeamName
        } = currentPlayerFromRoster;

        const teamName = getTeamName({
          team,
          teamName: rosterTeamName,
          teamMarket
        });

        const description =
          playersFromRoster.find(
            (prospectiveOpponentPlayer) =>
              prospectiveOpponentPlayer.team !== currentPlayerFromRoster?.team
          )?.team || '';

        /*
        should use awayTeamAbbreviation and homeTeamAbbreviation from Game, but finding it inconsistent.
        Until then, we will use this jank method that will only work for team games.
        const description =
        gamePlayer?.team === homeTeamAbbreviation
          ? awayTeamAbbreviation
          : homeTeamAbbreviation;
          */

        const playerDescriptors = {
          playerDescription: '',
          playerImage: currentPlayerFromRoster.image_url,
          playerName: currentPlayerFromRoster.name,
          secondaryHeader: ''
        };

        const projections = playerMarketOdds.flatMap((market) => {
          const marketStatTypeMap = allMarkets.find(
            (leagueMarket) => leagueMarket.market === market.market
          );

          const statTypeName = statTypes.find(
            (statType) =>
              statType.leagueProjectionFilterId ===
              String(marketStatTypeMap?.stat_type_id)
          );

          const oldProjection = existingProjections?.find(
            (existingProjection) => {
              return (
                existingProjection.foreignId === foreignId &&
                Number(existingProjection.statTypeId) ===
                  Number(marketStatTypeMap?.stat_type_id) &&
                existingProjection.oddsType === 'standard' &&
                existingProjection.isPromo === false
              );
            }
          );

          const { projectionId } = oldProjection || {};

          const {
            boardTime: oldProjectionBoardTime,
            startTime: oldProjectionStartTime
          } = oldProjection || {};

          const {
            boardTime: sampleProjectionBoardTime,
            startTime: sampleProjectionStartTime
          } = existingProjections?.[0] || {};

          const startTime =
            oldProjectionStartTime ||
            sampleProjectionStartTime ||
            gameScheduledAt;
          const boardTime =
            oldProjectionBoardTime ||
            sampleProjectionBoardTime ||
            DateTime.fromISO(gameScheduledAt).minus({ hour: 1 }).toISO() ||
            '';

          // extracting devigged implied prob form market
          let deviggedImpliedProb = market.median_lines.simulation_runs.find(
            (run) => run.simulation_run_id === simulationId
          )?.odds?.devigged?.implied_prob;

          if (deviggedImpliedProb === undefined) {
            deviggedImpliedProb = '0';
          }

          const medianLine = market.median_lines.simulation_runs.find(
            (run) => run.simulation_run_id === simulationId
          )?.line;

          if (medianLine === undefined) {
            return [];
          }

          const draft = !oldProjection;
          const rank = oldProjection?.rank || 0;
          const starterProjection = generateBaseProjection({
            autoTrade: false,
            boardTime,
            dataSource: 'simulation',
            description,
            draft,
            durationId,
            foreignId,
            gameId,
            startTime,
            displayTime: startTime,
            playerDescriptors,
            playerId,
            projectionValue: Number(medianLine),
            projectionId,
            position,
            rank,
            simsProbabilityOfMore: Number(deviggedImpliedProb),
            status: 'removed',
            statTypes: statTypeName ? [statTypeName] : [],
            team,
            teamName
          });

          return starterProjection ? [starterProjection] : [];
        });

        return projections;
      }),
    [
      allMarkets,
      durations,
      existingProjections,
      gameId,
      gameScheduledAt,
      simulationOddsData,
      playersFromRoster,
      simulationId,
      statTypes
    ]
  );

  return {
    isSimulationOddsError,
    isSimulationOddsLoading,
    simulationOdds: simulationOddsData,
    simulationGeneratedProjections
  };
}
